/**
 * Hook stateful pour la gestion des strokes en mode audience.
 *
 * Responsabilités :
 *  1. Load initial : GET /api/v1/pdfs/{id}/strokes au mount.
 *  2. Append local : appel `addStroke(draft)` après pointerup —
 *     pousse dans un buffer `pending`.
 *  3. Sync auto : `flushPending()` est appelée toutes les 2s (ou
 *     manuellement à l'unmount via beacon).
 *  4. Erase : supprime localement + appel DELETE backend.
 *  5. Reset page : appelle DELETE /pages/{n}/strokes + vide local.
 *  6. Undo : retire le dernier stroke pending (si pas encore sync)
 *     OU appelle DELETE serveur si déjà sync.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from '../stores/toaster';
import {
  bulkCreateStrokes,
  deleteStroke,
  listStrokes,
  resetPageStrokes,
  type Stroke,
  type StrokeDraftInput,
} from '../lib/strokes';
import type { LocalStroke } from './StrokeCanvas';

const SYNC_INTERVAL_MS = 2000;

function tempId(): string {
  return 'tmp-' + Math.random().toString(36).slice(2, 11) + '-' + Date.now().toString(36);
}

function serverToLocal(s: Stroke): LocalStroke {
  return {
    id: s.id,
    serverId: s.id,
    pending: false,
    draft: {
      page_number: s.page_number,
      stroke_data: s.stroke_data,
      color: s.color,
      width: s.width,
      tool: s.tool,
    },
  };
}

export function useStrokes(pdfId: string | undefined) {
  const [strokes, setStrokes] = useState<LocalStroke[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const flushingRef = useRef(false);
  const pdfIdRef = useRef(pdfId);
  pdfIdRef.current = pdfId;

  // Load initial à chaque changement de pdfId
  useEffect(() => {
    setStrokes([]);
    setPendingCount(0);
    if (!pdfId) return;
    let cancelled = false;
    (async () => {
      try {
        const rows = await listStrokes(pdfId);
        if (cancelled) return;
        setStrokes(rows.map(serverToLocal));
      } catch (err) {
        if (!cancelled) {
          console.warn('useStrokes load failed', err);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pdfId]);

  const flushPending = useCallback(async () => {
    const currentPdfId = pdfIdRef.current;
    if (!currentPdfId || flushingRef.current) return;
    flushingRef.current = true;
    try {
      // Snapshot des pending au moment de l'appel
      let toSync: LocalStroke[] = [];
      setStrokes((prev) => {
        toSync = prev.filter((s) => s.pending && !s.deleted);
        return prev;
      });
      if (toSync.length === 0) {
        return;
      }
      const drafts: StrokeDraftInput[] = toSync.map((s) => s.draft);
      const saved = await bulkCreateStrokes(currentPdfId, drafts);
      // Match l'ordre des saved au snapshot — l'API retourne dans le même ordre.
      setStrokes((prev) => {
        const next = prev.slice();
        let savedIdx = 0;
        for (const s of toSync) {
          const localIdx = next.findIndex((x) => x.id === s.id);
          if (localIdx >= 0 && savedIdx < saved.length) {
            const serverRow = saved[savedIdx]!;
            next[localIdx] = {
              ...next[localIdx]!,
              pending: false,
              serverId: serverRow.id,
              id: serverRow.id,
            };
            savedIdx++;
          }
        }
        return next;
      });
    } catch (err) {
      console.warn('useStrokes flush failed', err);
      toast.error('Sync stylet échouée — sera réessayée');
    } finally {
      flushingRef.current = false;
    }
  }, []);

  // Update pendingCount à chaque mutation strokes
  useEffect(() => {
    setPendingCount(strokes.filter((s) => s.pending && !s.deleted).length);
  }, [strokes]);

  // Cycle de sync auto
  useEffect(() => {
    if (!pdfId) return;
    const id = window.setInterval(() => {
      void flushPending();
    }, SYNC_INTERVAL_MS);
    return () => {
      window.clearInterval(id);
    };
  }, [pdfId, flushPending]);

  // Flush au unmount + à pagehide (tablette qui se met en veille)
  useEffect(() => {
    const handler = () => {
      void flushPending();
    };
    window.addEventListener('pagehide', handler);
    window.addEventListener('beforeunload', handler);
    return () => {
      window.removeEventListener('pagehide', handler);
      window.removeEventListener('beforeunload', handler);
      void flushPending();
    };
  }, [flushPending]);

  const addStroke = useCallback((draft: StrokeDraftInput) => {
    setStrokes((prev) => [
      ...prev,
      {
        id: tempId(),
        pending: true,
        draft,
      },
    ]);
  }, []);

  const eraseStroke = useCallback(async (localId: string) => {
    // Mark deleted local + DELETE backend si déjà sync
    let toDelete: LocalStroke | undefined;
    setStrokes((prev) => {
      const next = prev.slice();
      const idx = next.findIndex((s) => s.id === localId);
      if (idx >= 0) {
        toDelete = next[idx];
        if (next[idx]!.pending) {
          // Pas encore sync → on retire complètement
          next.splice(idx, 1);
        } else {
          next[idx] = { ...next[idx]!, deleted: true };
        }
      }
      return next;
    });
    if (toDelete && !toDelete.pending && toDelete.serverId) {
      try {
        await deleteStroke(toDelete.serverId);
        // Retire définitivement de la liste après succès
        setStrokes((prev) => prev.filter((s) => s.serverId !== toDelete!.serverId));
      } catch (err) {
        console.warn('useStrokes erase failed', err);
        toast.error('Suppression stylet échouée');
      }
    }
  }, []);

  const undoLast = useCallback(() => {
    setStrokes((prev) => {
      const next = prev.slice();
      // Cherche le dernier stroke visible (non deleted)
      for (let i = next.length - 1; i >= 0; i--) {
        if (!next[i]!.deleted) {
          const s = next[i]!;
          if (s.pending) {
            next.splice(i, 1);
          } else if (s.serverId) {
            next[i] = { ...s, deleted: true };
            // Fire DELETE async
            void deleteStroke(s.serverId).catch(() => {
              toast.error('Undo : suppression backend échouée');
            });
          }
          break;
        }
      }
      return next;
    });
  }, []);

  const resetPage = useCallback(async (pageNumber: number) => {
    const currentPdfId = pdfIdRef.current;
    if (!currentPdfId) return;
    // Optimistic local clear
    setStrokes((prev) => prev.filter((s) => s.draft.page_number !== pageNumber));
    try {
      await resetPageStrokes(currentPdfId, pageNumber);
    } catch (err) {
      console.warn('useStrokes resetPage failed', err);
      toast.error('Reset page stylet échoué');
      // Recharge depuis backend pour récupérer l'état réel
      try {
        const rows = await listStrokes(currentPdfId);
        setStrokes(rows.map(serverToLocal));
      } catch {
        /* ignore */
      }
    }
  }, []);

  const strokesByPage = useCallback(
    (pageNumber: number): LocalStroke[] => {
      return strokes.filter((s) => s.draft.page_number === pageNumber);
    },
    [strokes]
  );

  return {
    strokes,
    strokesByPage,
    pendingCount,
    addStroke,
    eraseStroke,
    undoLast,
    resetPage,
    flushPending,
  };
}
